import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  ShieldCheck, ShieldAlert, CheckCircle2, XCircle, AlertCircle, FileUp, Info,
  ChevronRight, Loader2, RefreshCw, Upload, FileText, ArrowUp, Lock, Globe, Package, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

// Tier definitions with upgrade requirements
const TIER_CONFIG = {
  tier_1_pan_only: {
    level: 1,
    name: 'Tier 1',
    title: 'PAN Only',
    description: 'Lead generation only - cannot process transactions',
    color: 'bg-gray-100 text-gray-800',
    icon: ShieldAlert,
    capabilities: ['Create listings', 'Receive enquiries', 'Lead forms only'],
    restrictions: 'No direct transactions'
  },
  tier_2_gst_enrollment: {
    level: 2,
    name: 'Tier 2',
    title: 'GST Enrollment',
    description: 'Intra-state transactions with limits (₹20 Lakhs/year)',
    color: 'bg-blue-100 text-blue-800',
    icon: ShieldCheck,
    capabilities: ['All Tier 1 features', 'Process orders within your state', 'Show Buy Now button to same-state customers'],
    restrictions: 'State-restricted sales only'
  },
  tier_3_full_gst: {
    level: 3,
    name: 'Tier 3',
    title: 'Full GST',
    description: 'Pan-India transactions - no limits',
    color: 'bg-green-100 text-green-800',
    icon: ShieldCheck,
    capabilities: ['All Tier 2 features', 'Sell across all Indian states', 'Unlimited transaction volume'],
    restrictions: 'Domestic only'
  },
  tier_4_gst_iec: {
    level: 4,
    name: 'Tier 4',
    title: 'International',
    description: 'Domestic + International transactions',
    color: 'bg-purple-100 text-purple-800',
    icon: Globe,
    capabilities: ['All Tier 3 features', 'International transactions', 'Export capabilities'],
    restrictions: 'None'
  },
  tier_5_gst_exempt: {
    level: 5,
    name: 'Tier 5',
    title: 'GST Exempt',
    description: 'Pan-India · No GST collected · Exempt by law',
    color: 'bg-green-100 text-green-800',
    icon: ShieldCheck,
    capabilities: ['Pan-India transactions (no GST charge)', 'All listing types', 'TDS/TCS apply on payouts'],
    restrictions: 'No international · No GST billing to customers',
    parallelTrack: true   // Not part of the upgrade ladder
  }
};

const TIER_ORDER = ['tier_1_pan_only', 'tier_2_gst_enrollment', 'tier_3_full_gst', 'tier_4_gst_iec'];
// Note: tier_5_gst_exempt is a parallel track — not in TIER_ORDER upgrade ladder

// Editable pending request card - shows submitted details and allows editing/resubmission
const PendingRequestDetailsCard = ({ compliance, apiCall, onResubmitted }) => {
  const [eligibility, setEligibility] = useState(null);
  const [loadingEligibility, setLoadingEligibility] = useState(true);
  const [editing, setEditing] = useState(false);
  const [resubmitting, setResubmitting] = useState(false);

  const [editForm, setEditForm] = useState({
    enrollment_id: compliance?.gst_enrollment_id || '',
    enrollment_verified: compliance?.enrollment_verified || false,
    gstin: compliance?.gstin || '',
    gst_verified: compliance?.gst_verified || false,
    gst_legal_name: compliance?.gst_business_name || '',
    gst_state: compliance?.gst_state || '',
    gst_doc: compliance?.gst_doc || null,
    iec_code: compliance?.iec_code || '',
    iec_holder_name: compliance?.iec_holder_name || '',
    iec_doc: compliance?.iec_doc || null,
    lut_acknowledged: compliance?.lut_acknowledged || false,
  });

  const requestedTier = compliance?.requested_tier;
  const requestedTierConfig = requestedTier ? TIER_CONFIG[requestedTier] : null;

  useEffect(() => {
    const checkEligibility = async () => {
      try {
        const data = await apiCall('/api/vendor-compliance/resubmission-eligibility');
        setEligibility(data);
      } catch (e) {
        console.error('Failed to check resubmission eligibility:', e);
      } finally {
        setLoadingEligibility(false);
      }
    };
    checkEligibility();
  }, [apiCall]);

  const handleFileUpload = async (file, fieldName) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', fieldName);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-compliance/upload-document`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` },
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        setEditForm(prev => ({ ...prev, [fieldName]: data.document_url }));
        toast.success('Document uploaded successfully');
      } else {
        toast.error(data.message || 'Failed to upload document');
      }
    } catch {
      toast.error('Failed to upload document');
    }
  };

  const handleResubmit = async () => {
    setResubmitting(true);
    try {
      await apiCall('/api/vendor-compliance/resubmit-upgrade', {
        method: 'POST',
        body: JSON.stringify({
          requested_tier: requestedTier,
          enrollment_id: editForm.enrollment_id,
          enrollment_verified: editForm.enrollment_verified,
          gstin: editForm.gstin,
          gst_verified: editForm.gst_verified,
          gst_legal_name: editForm.gst_legal_name,
          gst_state: editForm.gst_state,
          gst_doc: editForm.gst_doc,
          iec_code: editForm.iec_code,
          iec_holder_name: editForm.iec_holder_name,
          iec_doc: editForm.iec_doc,
          lut_acknowledged: editForm.lut_acknowledged,
        })
      });
      toast.success('Upgrade request resubmitted successfully');
      setEditing(false);
      onResubmitted();
    } catch (error) {
      const detail = error?.detail || error?.message || 'Failed to resubmit';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setResubmitting(false);
    }
  };

  // Renders a single detail row: label + value (read) or input (edit)
  const DetailRow = ({ label, value, field, type = 'text', placeholder = '' }) => (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 py-2 border-b border-amber-100 last:border-0">
      <span className="text-sm font-medium text-gray-600 sm:w-40 shrink-0">{label}</span>
      {editing ? (
        <Input
          value={editForm[field] || ''}
          onChange={(e) => setEditForm(prev => ({ ...prev, [field]: type === 'upper' ? e.target.value.toUpperCase() : e.target.value }))}
          placeholder={placeholder}
          className="flex-1 bg-white"
          data-testid={`edit-${field}`}
        />
      ) : (
        <span className="text-sm text-gray-800" data-testid={`detail-${field}`}>{value || '—'}</span>
      )}
    </div>
  );

  const DocumentRow = ({ label, url, field }) => (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 py-2 border-b border-amber-100 last:border-0">
      <span className="text-sm font-medium text-gray-600 sm:w-40 shrink-0">{label}</span>
      {editing ? (
        <div className="flex-1 flex items-center gap-2">
          {editForm[field] ? (
            <Badge className="bg-green-100 text-green-700 text-xs"><FileText className="h-3 w-3 mr-1" /> Uploaded</Badge>
          ) : (
            <Badge variant="outline" className="text-xs text-gray-500">No file</Badge>
          )}
          <label className="cursor-pointer text-xs text-primary hover:underline">
            {editForm[field] ? 'Replace' : 'Upload'}
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => handleFileUpload(e.target.files[0], field)} />
          </label>
        </div>
      ) : (
        <span className="text-sm" data-testid={`detail-${field}`}>
          {url ? (
            <Badge className="bg-green-100 text-green-700 text-xs"><FileText className="h-3 w-3 mr-1" /> Uploaded</Badge>
          ) : (
            <span className="text-gray-400">Not uploaded</span>
          )}
        </span>
      )}
    </div>
  );

  const VerifiedBadge = ({ verified }) => (
    verified
      ? <Badge className="bg-green-100 text-green-700 text-xs ml-2"><CheckCircle2 className="h-3 w-3 mr-1" /> Verified</Badge>
      : <Badge variant="outline" className="text-xs text-gray-500 ml-2">Not verified</Badge>
  );

  return (
    <Card className="border-amber-200 bg-amber-50/50" data-testid="pending-request-details-card">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            Upgrade Request Pending
          </CardTitle>
          <Badge variant="secondary" className="bg-amber-100 text-amber-800 w-fit">
            Under Admin Review
          </Badge>
        </div>
        {requestedTierConfig && (
          <CardDescription className="text-amber-700">
            Requesting upgrade to <strong>{requestedTierConfig.name} ({requestedTierConfig.title})</strong>
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Eligibility & timing info */}
        {!loadingEligibility && eligibility && (
          <div className="flex flex-wrap items-center gap-3 text-sm text-amber-700">
            <span>Pending for <strong>{eligibility.days_pending} day(s)</strong></span>
            {eligibility.resubmission_count > 0 && (
              <Badge variant="outline" className="text-xs border-amber-300">Resubmitted {eligibility.resubmission_count}x</Badge>
            )}
          </div>
        )}
        {loadingEligibility && (
          <div className="flex items-center gap-2 text-xs text-amber-600">
            <Loader2 className="h-3 w-3 animate-spin" /> Checking eligibility...
          </div>
        )}

        {/* Submitted Details */}
        <div className="bg-white rounded-lg border border-amber-200 p-4" data-testid="submitted-details-section">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-800 text-sm">Submitted Details</h4>
            {!editing && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-amber-700 hover:text-amber-900 hover:bg-amber-100"
                onClick={() => setEditing(true)}
                data-testid="edit-details-btn"
              >
                <RefreshCw className="h-3 w-3 mr-1" /> Edit Details
              </Button>
            )}
            {editing && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-gray-500"
                onClick={() => {
                  setEditing(false);
                  // Reset form to original compliance values
                  setEditForm({
                    enrollment_id: compliance?.gst_enrollment_id || '',
                    enrollment_verified: compliance?.enrollment_verified || false,
                    gstin: compliance?.gstin || '',
                    gst_verified: compliance?.gst_verified || false,
                    gst_legal_name: compliance?.gst_business_name || '',
                    gst_state: compliance?.gst_state || '',
                    gst_doc: compliance?.gst_doc || null,
                    iec_code: compliance?.iec_code || '',
                    iec_holder_name: compliance?.iec_holder_name || '',
                    iec_doc: compliance?.iec_doc || null,
                    lut_acknowledged: compliance?.lut_acknowledged || false,
                  });
                }}
                data-testid="cancel-edit-btn"
              >
                Cancel
              </Button>
            )}
          </div>

          {/* Tier 2 details */}
          {requestedTier === 'tier_2_gst_enrollment' && (
            <div className="space-y-0">
              <DetailRow label="Enrollment ID" value={compliance?.gst_enrollment_id} field="enrollment_id" type="upper" placeholder="Enter Enrollment ID" />
              <div className="flex items-center py-2">
                <span className="text-sm font-medium text-gray-600 sm:w-40 shrink-0">Verification</span>
                <VerifiedBadge verified={compliance?.enrollment_verified} />
              </div>
            </div>
          )}

          {/* Tier 3 details */}
          {requestedTier === 'tier_3_full_gst' && (
            <div className="space-y-0">
              <DetailRow label="GSTIN" value={compliance?.gstin} field="gstin" type="upper" placeholder="22ABCDE1234F1Z5" />
              <div className="flex items-center py-2 border-b border-amber-100">
                <span className="text-sm font-medium text-gray-600 sm:w-40 shrink-0">GST Verification</span>
                <VerifiedBadge verified={compliance?.gst_verified} />
              </div>
              <DetailRow label="Legal Name" value={compliance?.gst_business_name} field="gst_legal_name" placeholder="Business legal name" />
              <DetailRow label="State" value={compliance?.gst_state} field="gst_state" placeholder="State" />
              <DocumentRow label="GST Certificate" url={compliance?.gst_doc} field="gst_doc" />
            </div>
          )}

          {/* Tier 4 details */}
          {requestedTier === 'tier_4_gst_iec' && (
            <div className="space-y-0">
              {compliance?.lut_acknowledged ? (
                <div className="flex items-center py-2 border-b border-amber-100">
                  <span className="text-sm font-medium text-gray-600 sm:w-40 shrink-0">LUT Compliance</span>
                  <Badge className="bg-green-100 text-green-700 text-xs"><CheckCircle2 className="h-3 w-3 mr-1" /> Acknowledged</Badge>
                  {editing && (
                    <label className="flex items-center gap-2 ml-4 text-xs text-gray-500 cursor-pointer">
                      <Checkbox
                        checked={editForm.lut_acknowledged}
                        onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, lut_acknowledged: checked }))}
                      />
                      LUT Acknowledged
                    </label>
                  )}
                </div>
              ) : (
                <>
                  <DetailRow label="IEC Code" value={compliance?.iec_code} field="iec_code" type="upper" placeholder="10-digit IEC Code" />
                  <DetailRow label="IEC Holder Name" value={compliance?.iec_holder_name} field="iec_holder_name" placeholder="Name as per IEC" />
                  <DocumentRow label="IEC Certificate" url={compliance?.iec_doc} field="iec_doc" />
                </>
              )}
            </div>
          )}

          {/* No specific tier data to show */}
          {!requestedTier && (
            <p className="text-sm text-gray-500">Compliance details are being reviewed.</p>
          )}
        </div>

        {/* Resubmit action */}
        {editing && (
          <div className="pt-2">
            {!loadingEligibility && eligibility?.eligible ? (
              <Button
                onClick={handleResubmit}
                disabled={resubmitting}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="resubmit-upgrade-btn"
              >
                {resubmitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Resubmitting...</>
                ) : (
                  <><RefreshCw className="h-4 w-4 mr-2" /> Resubmit Upgrade Request</>
                )}
              </Button>
            ) : (
              <div className="text-center space-y-2">
                <Button disabled className="w-full" variant="outline">
                  <Lock className="h-4 w-4 mr-2" /> Resubmission Not Yet Available
                </Button>
                {eligibility && (
                  <p className="text-xs text-amber-600">
                    Resubmission available after {eligibility.threshold_days} days of pending ({eligibility.days_pending} day(s) so far).
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Quick resubmit banner when not editing */}
        {!editing && !loadingEligibility && eligibility?.eligible && (
          <div className="flex items-center justify-between p-3 bg-amber-100 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-800">
              Pending for over {eligibility.threshold_days} days. You can edit and resubmit.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-500 text-amber-800 hover:bg-amber-200 shrink-0"
              onClick={() => setEditing(true)}
              data-testid="start-edit-resubmit-btn"
            >
              Edit & Resubmit
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const VendorComplianceStatus = () => {
  const { apiCall } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [compliance, setCompliance] = useState(null);
  const [vendorListingTypes, setVendorListingTypes] = useState([]);
  
  // CAPTCHA state for verification
  const [captchaData, setCaptchaData] = useState({
    sessionId: null,
    captchaImage: null,
    captchaInput: '',
    loading: false,
    showCaptcha: false
  });
  
  // Enrollment verification result
  const [enrollmentVerificationResult, setEnrollmentVerificationResult] = useState(null);
  
  // Form state for upgrade
  const [upgradeForm, setUpgradeForm] = useState({
    // Tier 2 fields
    enrollment_id: '',
    enrollment_verified: false,
    enrollment_doc: null,
    // Tier 3 fields
    gstin: '',
    gst_verified: false,
    gst_doc: null,
    gst_legal_name: '',
    gst_trade_name: '',
    gst_business_address: '',
    gst_state: '',
    // Tier 4 fields
    iec_code: '',
    iec_holder_name: '',
    iec_doc: null,
    lut_acknowledged: false
  });

  const fetchCompliance = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiCall('/api/vendor-compliance/my-compliance');
      setCompliance(response);
      
      // Pre-fill form with existing data
      if (response) {
        setUpgradeForm(prev => ({
          ...prev,
          enrollment_id: response.gst_enrollment_id || '',
          enrollment_verified: response.enrollment_verified || false,
          gstin: response.gstin || '',
          gst_verified: response.gst_verified || false,
          gst_legal_name: response.gst_business_name || '',
          gst_state: response.gst_state || '',
          iec_code: response.iec_code || '',
          iec_holder_name: response.iec_holder_name || '',
          lut_acknowledged: response.lut_acknowledged || false
        }));
      }
      
      // Fetch vendor's listing types to determine product/service
      try {
        const listings = await apiCall('/api/listings/');
        if (listings && listings.length > 0) {
          const types = [...new Set(listings.map(l => l.listing_type))];
          setVendorListingTypes(types);
        }
      } catch (e) {
      }
    } catch (error) {
      console.error('Error fetching compliance:', error);
      toast.error('Failed to load compliance status');
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchCompliance();
  }, [fetchCompliance]);

  const currentTier = compliance?.current_tier || 'tier_1_pan_only';
  const currentTierConfig = TIER_CONFIG[currentTier];
  const currentTierIndex = TIER_ORDER.indexOf(currentTier);
  
  // Allow selection of any higher tier (not just next one)
  const [selectedUpgradeTier, setSelectedUpgradeTier] = useState(null);
  // Tier 5 is a parallel track — don't show it in the normal upgrade ladder
  const availableUpgradeTiers = currentTier === 'tier_5_gst_exempt'
    ? []   // Tier 5 vendors have no further upgrades in the standard ladder
    : TIER_ORDER.slice(currentTierIndex < 0 ? 0 : currentTierIndex + 1);
  const targetTier = selectedUpgradeTier || (availableUpgradeTiers.length > 0 ? availableUpgradeTiers[0] : null);
  const targetTierConfig = targetTier ? TIER_CONFIG[targetTier] : null;

  // Tier 5 — GST Exempt declaration state
  const [showExemptPanel, setShowExemptPanel] = useState(false);
  const [exemptForm, setExemptForm] = useState({ gst_exempt_category: '', gst_exempt_reason: '', gst_exempt_declared: false });
  const [exemptSubmitting, setExemptSubmitting] = useState(false);

  const submitExemptDeclaration = async () => {
    if (!exemptForm.gst_exempt_category) { toast.error('Please select your exempt supply category'); return; }
    if (!exemptForm.gst_exempt_declared) { toast.error('You must confirm that your supplies are GST-exempt'); return; }
    setExemptSubmitting(true);
    try {
      await apiCall('/api/vendor-compliance/submit-upgrade', {
        method: 'POST',
        body: JSON.stringify({ requested_tier: 'tier_5_gst_exempt', ...exemptForm })
      });
      toast.success('GST Exempt declaration submitted for admin review');
      setShowExemptPanel(false);
      fetchCompliance();
    } catch (e) {
      toast.error('Failed to submit declaration');
    } finally {
      setExemptSubmitting(false);
    }
  };
  
  // Determine if vendor is primarily product or service based
  const hasProducts = vendorListingTypes.includes('product');
  const hasServices = vendorListingTypes.includes('service');
  const isServiceOnly = hasServices && !hasProducts;

  // Fetch CAPTCHA for verification
  const fetchCaptcha = async () => {
    setCaptchaData(prev => ({ ...prev, loading: true }));
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gst-verification/captcha`);
      const data = await response.json();
      
      if (response.ok) {
        setCaptchaData({
          sessionId: data.session_id,
          captchaImage: data.captcha_image,
          captchaInput: '',
          loading: false,
          showCaptcha: true
        });
        toast.success('CAPTCHA loaded - please solve to verify');
      } else {
        throw new Error(data.detail || 'Failed to fetch CAPTCHA');
      }
    } catch (error) {
      setCaptchaData(prev => ({ ...prev, loading: false }));
      toast.error(error.message || 'Failed to load CAPTCHA');
    }
  };

  // Verify Enrollment ID (Tier 1 → Tier 2)
  const verifyEnrollment = async () => {
    if (!upgradeForm.enrollment_id) {
      toast.error('Please enter your Enrollment ID');
      return;
    }
    
    if (!captchaData.showCaptcha) {
      await fetchCaptcha();
      return;
    }
    
    if (!captchaData.captchaInput) {
      toast.error('Please enter the CAPTCHA');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gst-verification/verify-enrollment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: captchaData.sessionId,
          enrollment_id: upgradeForm.enrollment_id,
          captcha: captchaData.captchaInput
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setUpgradeForm(prev => ({ ...prev, enrollment_verified: true }));
        setEnrollmentVerificationResult({
          success: true,
          status: data.status,
          applicantName: data.applicant_name,
          tradeName: data.trade_name,
          applicationDate: data.application_date,
          state: data.state
        });
        setCaptchaData({ sessionId: null, captchaImage: null, captchaInput: '', loading: false, showCaptcha: false });
        toast.success('Enrollment ID verified successfully!');
      } else {
        setEnrollmentVerificationResult({
          success: false,
          error: data.error
        });
        setCaptchaData(prev => ({ ...prev, captchaInput: '' }));
        toast.error(data.error || 'Verification failed. Please try again.');
      }
    } catch (error) {
      toast.error('Verification failed. Please check your connection.');
    } finally {
      setSubmitting(false);
    }
  };

  // Verify GSTIN (Tier 2 → Tier 3)
  const verifyGSTIN = async () => {
    if (!upgradeForm.gstin || upgradeForm.gstin.length !== 15) {
      toast.error('Please enter a valid 15-character GSTIN');
      return;
    }
    
    if (!captchaData.showCaptcha) {
      await fetchCaptcha();
      return;
    }
    
    if (!captchaData.captchaInput) {
      toast.error('Please enter the CAPTCHA');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gst-verification/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: captchaData.sessionId,
          gstin: upgradeForm.gstin,
          captcha: captchaData.captchaInput
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setUpgradeForm(prev => ({
          ...prev,
          gst_verified: true,
          gst_legal_name: data.legal_name || '',
          gst_trade_name: data.trade_name || '',
          gst_state: data.state_jurisdiction || ''
        }));
        setCaptchaData({ sessionId: null, captchaImage: null, captchaInput: '', loading: false, showCaptcha: false });
        toast.success('GSTIN verified successfully!');
      } else {
        setCaptchaData(prev => ({ ...prev, captchaInput: '' }));
        toast.error(data.error || 'Verification failed. Please try again.');
      }
    } catch (error) {
      toast.error('Verification failed. Please check your connection.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (file, fieldName) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', fieldName);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-compliance/upload-document`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        },
        body: formData
      });
      
      const data = await response.json();
      if (data.success) {
        setUpgradeForm(prev => ({ ...prev, [fieldName]: data.document_url }));
        toast.success('Document uploaded successfully');
      } else {
        toast.error(data.message || 'Failed to upload document');
      }
    } catch (error) {
      toast.error('Failed to upload document');
    }
  };

  // Submit upgrade request
  const submitUpgradeRequest = async () => {
    if (!targetTier) return;
    
    // Validation based on target tier
    if (targetTier === 'tier_2_gst_enrollment') {
      if (!upgradeForm.enrollment_verified) {
        toast.error('Please verify your Enrollment ID first');
        return;
      }
    } else if (targetTier === 'tier_3_full_gst') {
      if (!upgradeForm.gst_verified) {
        toast.error('Please verify your GSTIN first');
        return;
      }
      if (!upgradeForm.gst_doc) {
        toast.error('Please upload your GST Certificate');
        return;
      }
    } else if (targetTier === 'tier_4_gst_iec') {
      if (isServiceOnly) {
        if (!upgradeForm.lut_acknowledged) {
          toast.error('Please acknowledge the LUT requirement');
          return;
        }
      } else {
        if (!upgradeForm.iec_code) {
          toast.error('Please enter your IEC Code');
          return;
        }
        if (!upgradeForm.iec_doc) {
          toast.error('Please upload your IEC Certificate');
          return;
        }
      }
    }
    
    setSubmitting(true);
    try {
      await apiCall('/api/vendor-compliance/submit-upgrade', {
        method: 'POST',
        body: JSON.stringify({
          requested_tier: targetTier,
          ...upgradeForm
        })
      });
      
      toast.success('Upgrade request submitted for admin review');
      fetchCompliance();
    } catch (error) {
      toast.error('Failed to submit upgrade request');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = () => {
    if (!compliance) return null;
    
    const statusConfig = {
      pending: { label: 'Pending Review', variant: 'secondary', icon: AlertCircle },
      approved: { label: 'Verified', variant: 'default', icon: CheckCircle2 },
      rejected: { label: 'Rejected', variant: 'destructive', icon: XCircle }
    };
    
    const config = statusConfig[compliance.status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto px-4 pb-24 md:pb-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Compliance Status</h1>
          <p className="text-gray-500 mt-1">Manage your vendor compliance tier and unlock more selling capabilities</p>
        </div>

        {/* Current Tier Status */}
        <Card className="border-2">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <CardTitle className="flex items-center gap-2">
                <currentTierConfig.icon className="h-5 w-5" />
                Current Tier
              </CardTitle>
              {getStatusBadge()}
            </div>
          </CardHeader>
          <CardContent>
            <div className={`p-4 rounded-lg ${currentTierConfig.color}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                <div>
                  <h3 className="font-bold text-lg">{currentTierConfig.name}: {currentTierConfig.title}</h3>
                  <p className="text-sm opacity-80">{currentTierConfig.description}</p>
                </div>
                <Badge variant="outline" className="w-fit">
                  Level {currentTierConfig.level}/4
                </Badge>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium mb-1">Capabilities:</p>
                  <ul className="space-y-1">
                    {currentTierConfig.capabilities.map((cap, i) => (
                      <li key={cap.id || cap.name || `${cap.title || "cap"}-${i}`} className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>{cap}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-1">Restrictions:</p>
                  <p className="flex items-start gap-2">
                    <Lock className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{currentTierConfig.restrictions}</span>
                  </p>
                </div>
              </div>
            </div>
            
            {compliance?.status === 'rejected' && compliance?.rejection_reason && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm font-medium text-red-800 flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Previous Upgrade Rejected
                </p>
                <p className="text-sm text-red-700 mt-1">{compliance.rejection_reason}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tier Progress */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Compliance Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              {TIER_ORDER.map((tier, index) => {
                const config = TIER_CONFIG[tier];
                const isComplete = index <= currentTierIndex && compliance?.status === 'approved';
                const isCurrent = tier === currentTier;
                const isPending = tier === currentTier && compliance?.status === 'pending';
                
                return (
                  <React.Fragment key={tier}>
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                        isComplete ? 'bg-green-500 border-green-500 text-white' :
                        isPending ? 'bg-yellow-100 border-yellow-500 text-yellow-700' :
                        isCurrent ? 'bg-blue-100 border-blue-500 text-blue-700' :
                        'bg-gray-100 border-gray-300 text-gray-400'
                      }`}>
                        {isComplete ? <CheckCircle2 className="h-5 w-5" /> : config.level}
                      </div>
                      <span className={`text-xs mt-1 text-center hidden sm:block ${
                        isCurrent ? 'font-medium text-blue-700' : 'text-gray-500'
                      }`}>
                        {config.title}
                      </span>
                    </div>
                    {index < 3 && (
                      <div className={`flex-1 h-1 mx-2 rounded ${
                        index < currentTierIndex ? 'bg-green-500' : 'bg-gray-200'
                      }`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Upgrade Section */}
        {availableUpgradeTiers.length > 0 && compliance?.status !== 'pending' && (
          <Card className="border-2 border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ArrowUp className="h-5 w-5 text-primary" />
                Upgrade Your Compliance Tier
              </CardTitle>
              <CardDescription>Choose a tier to upgrade to - you can skip intermediate tiers if you have the required documents</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Tier Selection */}
              <div className="space-y-3">
                <Label>Select Target Tier</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {availableUpgradeTiers.map((tier) => {
                    const config = TIER_CONFIG[tier];
                    const isSelected = targetTier === tier;
                    return (
                      <button
                        key={tier}
                        onClick={() => setSelectedUpgradeTier(tier)}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          isSelected 
                            ? 'border-primary bg-primary/5' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <config.icon className={`h-5 w-5 ${isSelected ? 'text-primary' : 'text-gray-500'}`} />
                          <span className="font-medium">{config.name}</span>
                        </div>
                        <p className="text-sm text-gray-600">{config.title}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tier 2: Enrollment ID Verification */}
              {targetTier === 'tier_2_gst_enrollment' && (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium flex items-center gap-2 mb-2">
                      <Info className="h-4 w-4" />
                      Requirements for Tier 2 (Enrollment ID)
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Valid GST Enrollment ID (Composition/Provisional)</li>
                      <li>• Verification via GST Portal with CAPTCHA</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-3">
                    <Label htmlFor="enrollment_id">GST Enrollment ID *</Label>
                    <div className="flex gap-2">
                      <Input
                        id="enrollment_id"
                        value={upgradeForm.enrollment_id}
                        onChange={(e) => setUpgradeForm(prev => ({ ...prev, enrollment_id: e.target.value.toUpperCase() }))}
                        placeholder="Enter your Enrollment ID"
                        disabled={upgradeForm.enrollment_verified}
                        className="flex-1"
                      />
                      {upgradeForm.enrollment_verified ? (
                        <Badge className="bg-green-100 text-green-800 h-10 px-3">
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Verified
                        </Badge>
                      ) : (
                        <Button onClick={verifyEnrollment} disabled={submitting || captchaData.loading}>
                          {captchaData.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* CAPTCHA Section with Submit Button */}
                  {captchaData.showCaptcha && !upgradeForm.enrollment_verified && (
                    <div className="p-4 border rounded-lg space-y-3 bg-gray-50">
                      <Label>Solve CAPTCHA to verify</Label>
                      <div className="flex items-center gap-4">
                        {captchaData.captchaImage && (
                          <img src={captchaData.captchaImage} alt="CAPTCHA" className="h-12 border rounded" />
                        )}
                        <Button variant="outline" size="sm" onClick={fetchCaptcha}>
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      </div>
                      <Input
                        value={captchaData.captchaInput}
                        onChange={(e) => setCaptchaData(prev => ({ ...prev, captchaInput: e.target.value }))}
                        placeholder="Enter CAPTCHA"
                      />
                      <Button 
                        onClick={verifyEnrollment} 
                        disabled={submitting || !captchaData.captchaInput}
                        className="w-full"
                      >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Submit CAPTCHA & Verify
                      </Button>
                    </div>
                  )}
                  
                  {/* Enrollment Verification Result */}
                  {upgradeForm.enrollment_verified && enrollmentVerificationResult?.success && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <h4 className="font-medium text-green-800 flex items-center gap-2 mb-3">
                        <CheckCircle2 className="h-5 w-5" />
                        Enrollment ID Verified Successfully
                      </h4>
                      <div className="grid grid-cols-2 gap-3 text-sm text-green-700">
                        {enrollmentVerificationResult.status && (
                          <div>
                            <span className="font-medium">Status:</span>
                            <div className="text-green-800 font-semibold">{enrollmentVerificationResult.status}</div>
                          </div>
                        )}
                        {enrollmentVerificationResult.applicantName && (
                          <div>
                            <span className="font-medium">Applicant Name:</span>
                            <div>{enrollmentVerificationResult.applicantName}</div>
                          </div>
                        )}
                        {enrollmentVerificationResult.tradeName && (
                          <div>
                            <span className="font-medium">Trade Name:</span>
                            <div>{enrollmentVerificationResult.tradeName}</div>
                          </div>
                        )}
                        {enrollmentVerificationResult.applicationDate && (
                          <div>
                            <span className="font-medium">Application Date:</span>
                            <div>{enrollmentVerificationResult.applicationDate}</div>
                          </div>
                        )}
                        {enrollmentVerificationResult.state && (
                          <div>
                            <span className="font-medium">State:</span>
                            <div>{enrollmentVerificationResult.state}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tier 3: Full GST Verification */}
              {targetTier === 'tier_3_full_gst' && (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-medium flex items-center gap-2 mb-2">
                      <Info className="h-4 w-4" />
                      Requirements for Tier 3 (Full GST)
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Valid GSTIN (15 characters)</li>
                      <li>• Verification via GST Portal with CAPTCHA</li>
                      <li>• GST Certificate document upload</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-3">
                    <Label htmlFor="gstin">GSTIN *</Label>
                    <div className="flex gap-2">
                      <Input
                        id="gstin"
                        value={upgradeForm.gstin}
                        onChange={(e) => setUpgradeForm(prev => ({ ...prev, gstin: e.target.value.toUpperCase() }))}
                        placeholder="22ABCDE1234F1Z5"
                        maxLength={15}
                        disabled={upgradeForm.gst_verified}
                        className="flex-1"
                      />
                      {upgradeForm.gst_verified ? (
                        <Badge className="bg-green-100 text-green-800 h-10 px-3">
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Verified
                        </Badge>
                      ) : (
                        <Button onClick={verifyGSTIN} disabled={submitting || captchaData.loading}>
                          {captchaData.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* CAPTCHA Section with Submit Button */}
                  {captchaData.showCaptcha && !upgradeForm.gst_verified && (
                    <div className="p-4 border rounded-lg space-y-3 bg-gray-50">
                      <Label>Solve CAPTCHA to verify</Label>
                      <div className="flex items-center gap-4">
                        {captchaData.captchaImage && (
                          <img src={captchaData.captchaImage} alt="CAPTCHA" className="h-12 border rounded" />
                        )}
                        <Button variant="outline" size="sm" onClick={fetchCaptcha}>
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      </div>
                      <Input
                        value={captchaData.captchaInput}
                        onChange={(e) => setCaptchaData(prev => ({ ...prev, captchaInput: e.target.value }))}
                        placeholder="Enter CAPTCHA"
                      />
                      <Button 
                        onClick={verifyGSTIN} 
                        disabled={submitting || !captchaData.captchaInput}
                        className="w-full"
                      >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Submit CAPTCHA & Verify
                      </Button>
                    </div>
                  )}
                  
                  {/* Verified GST Info */}
                  {upgradeForm.gst_verified && (
                    <div className="p-3 bg-gray-50 rounded-lg text-sm">
                      <p><strong>Legal Name:</strong> {upgradeForm.gst_legal_name}</p>
                      {upgradeForm.gst_trade_name && <p><strong>Trade Name:</strong> {upgradeForm.gst_trade_name}</p>}
                      {upgradeForm.gst_state && <p><strong>State:</strong> {upgradeForm.gst_state}</p>}
                    </div>
                  )}
                  
                  {/* GST Document Upload */}
                  <div className="space-y-2">
                    <Label>GST Certificate Upload *</Label>
                    <div className="border-2 border-dashed rounded-lg p-4 text-center">
                      {upgradeForm.gst_doc ? (
                        <div className="flex items-center justify-center gap-2 text-green-600">
                          <FileText className="h-5 w-5" />
                          <span>Document uploaded</span>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <div className="flex flex-col items-center gap-2 text-gray-500">
                            <Upload className="h-8 w-8" />
                            <span>Click to upload GST Certificate</span>
                            <span className="text-xs">PDF, JPG, PNG (Max 5MB)</span>
                          </div>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="hidden"
                            onChange={(e) => handleFileUpload(e.target.files[0], 'gst_doc')}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Tier 4: IEC or LUT */}
              {targetTier === 'tier_4_gst_iec' && (
                <div className="space-y-4">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h4 className="font-medium flex items-center gap-2 mb-2">
                      <Info className="h-4 w-4" />
                      Requirements for Tier 4 (International)
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li className="flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        <strong>For Product Sellers:</strong> IEC Code (Import Export Code)
                      </li>
                      <li className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4" />
                        <strong>For Service Providers:</strong> LUT Compliance Acknowledgement
                      </li>
                    </ul>
                  </div>
                  
                  {/* Show different UI based on vendor type */}
                  {isServiceOnly ? (
                    // Service providers - LUT Acknowledgement
                    <div className="space-y-4">
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <h4 className="font-medium text-amber-800 mb-2">LUT Compliance for Service Exports</h4>
                        <p className="text-sm text-amber-700 mb-3">
                          As a service provider with GST registration, you can export services internationally 
                          without paying IGST by filing a Letter of Undertaking (LUT) with the GST department.
                        </p>
                        <ul className="text-sm text-amber-700 space-y-1">
                          <li>• LUT must be filed before the start of each financial year</li>
                          <li>• File on GST Portal under Services → User Services → Furnish LUT</li>
                          <li>• Valid for the entire financial year</li>
                        </ul>
                      </div>
                      
                      <div className="flex items-start space-x-3 p-4 border rounded-lg">
                        <Checkbox
                          id="lut_acknowledged"
                          checked={upgradeForm.lut_acknowledged}
                          onCheckedChange={(checked) => setUpgradeForm(prev => ({ ...prev, lut_acknowledged: checked }))}
                        />
                        <label htmlFor="lut_acknowledged" className="text-sm cursor-pointer">
                          <strong>I acknowledge</strong> that I understand the LUT requirements for exporting services 
                          and will ensure proper LUT filing with the GST department before processing international orders.
                        </label>
                      </div>
                    </div>
                  ) : (
                    // Product sellers - IEC Code
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="iec_code">IEC Code *</Label>
                        <Input
                          id="iec_code"
                          value={upgradeForm.iec_code}
                          onChange={(e) => setUpgradeForm(prev => ({ ...prev, iec_code: e.target.value.toUpperCase() }))}
                          placeholder="Enter your 10-digit IEC Code"
                          maxLength={10}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="iec_holder_name">IEC Holder Name</Label>
                        <Input
                          id="iec_holder_name"
                          value={upgradeForm.iec_holder_name}
                          onChange={(e) => setUpgradeForm(prev => ({ ...prev, iec_holder_name: e.target.value }))}
                          placeholder="Name as per IEC Certificate"
                        />
                      </div>
                      
                      {/* IEC Document Upload */}
                      <div className="space-y-2">
                        <Label>IEC Certificate Upload *</Label>
                        <div className="border-2 border-dashed rounded-lg p-4 text-center">
                          {upgradeForm.iec_doc ? (
                            <div className="flex items-center justify-center gap-2 text-green-600">
                              <FileText className="h-5 w-5" />
                              <span>Document uploaded</span>
                            </div>
                          ) : (
                            <label className="cursor-pointer">
                              <div className="flex flex-col items-center gap-2 text-gray-500">
                                <Upload className="h-8 w-8" />
                                <span>Click to upload IEC Certificate</span>
                                <span className="text-xs">PDF, JPG, PNG (Max 5MB)</span>
                              </div>
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="hidden"
                                onChange={(e) => handleFileUpload(e.target.files[0], 'iec_doc')}
                              />
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <Button
                onClick={submitUpgradeRequest}
                disabled={submitting}
                className="w-full"
                size="lg"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Submitting...</>
                ) : (
                  <>Submit Upgrade Request to {targetTierConfig?.name} <ChevronRight className="h-4 w-4 ml-2" /></>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Pending Review Notice with Submitted Details */}
        {compliance?.status === 'pending' && (
          <PendingRequestDetailsCard
            compliance={compliance}
            apiCall={apiCall}
            onResubmitted={() => fetchCompliance()}
          />
        )}

        {/* Max Tier Reached */}
        {currentTier === 'tier_4_gst_iec' && compliance?.status === 'approved' && (
          <Card className="border-purple-200 bg-purple-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Globe className="h-5 w-5 text-purple-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-purple-800">Maximum Tier Achieved!</h4>
                  <p className="text-sm text-purple-700 mt-1">
                    Congratulations! You have achieved the highest compliance tier and can process both domestic and international transactions.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Special Track: Tier 5 GST Exempt ── */}
        {currentTier !== 'tier_5_gst_exempt' && compliance?.status !== 'pending' && (
          <Card className="border-green-200 bg-green-50 mt-4">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                  <div>
                    <h4 className="font-semibold text-green-800">Special Track: GST Exempt (Tier 5)</h4>
                    <p className="text-sm text-green-700 mt-1">
                      Applicable for: Doctors · Pujari · Religious goods · Educational services · Agricultural produce
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      If your supplies are exempt under the CGST Act, 2017 — you can sell Pan-India without collecting or remitting GST.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowExemptPanel(v => !v)}
                  className="shrink-0 text-sm font-medium text-green-700 underline whitespace-nowrap"
                  data-testid="declare-exemption-btn"
                >
                  {showExemptPanel ? 'Hide' : 'Declare Exemption →'}
                </button>
              </div>

              {showExemptPanel && (
                <div className="mt-4 space-y-3 border-t border-green-200 pt-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Exempt Supply Category *</label>
                    <select
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      value={exemptForm.gst_exempt_category}
                      onChange={e => setExemptForm(f => ({ ...f, gst_exempt_category: e.target.value }))}
                      data-testid="exempt-category-select"
                    >
                      <option value="">— Select exempt category —</option>
                      <option value="healthcare">Healthcare &amp; Medical Services (SAC 9993)</option>
                      <option value="religious_services">Religious &amp; Spiritual Services (SAC 9994)</option>
                      <option value="religious_goods">Religious / Puja Goods — Books, Idols, Samagri</option>
                      <option value="educational">Educational Services (SAC 9992)</option>
                      <option value="agricultural">Agricultural Produce</option>
                      <option value="charitable">Charitable / NGO Services</option>
                      <option value="other">Other GST-Exempt Supply</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Additional description <span className="font-normal text-gray-400">(optional)</span></label>
                    <input
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      placeholder="e.g. Ayurvedic consultation, temple prasad distribution..."
                      value={exemptForm.gst_exempt_reason}
                      onChange={e => setExemptForm(f => ({ ...f, gst_exempt_reason: e.target.value }))}
                      data-testid="exempt-reason"                    />
                  </div>
                  <label className="flex items-start gap-3 p-3 bg-white rounded-lg border border-green-200 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={exemptForm.gst_exempt_declared}
                      onChange={e => setExemptForm(f => ({ ...f, gst_exempt_declared: e.target.checked }))}
                      data-testid="exempt-declaration-checkbox"
                    />
                    <span className="text-sm text-gray-700">
                      I confirm that my primary business involves GST-exempt supplies as per the CGST Act, 2017.
                      I understand that misrepresentation may result in account suspension.
                    </span>
                  </label>
                  <button
                    onClick={submitExemptDeclaration}
                    disabled={exemptSubmitting}
                    className="w-full bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
                    data-testid="submit-exempt-btn"
                  >
                    {exemptSubmitting ? 'Submitting...' : 'Submit GST Exempt Declaration for Admin Review'}
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default VendorComplianceStatus;
